import { Injectable } from '@angular/core';

interface ParametrizacaoCacheada {
    /** Momento em que a parametrização foi guardada (epoch ms). */
    salvoEm: number;
    dados: any;
}

interface MapaParametrizacoesCacheadas {
    [chaveParametrizacao: string]: ParametrizacaoCacheada;
}

/**
 * Cache das parametrizações no localStorage.
 *
 * O endpoint `/rest/parametrizacoes/{chave}` devolve ~70 kB e é o mais lento da
 * carga dos formulários, enquanto a parametrização de um edital praticamente não
 * muda depois de publicada. As entradas são indexadas pela chave de
 * parametrização (a mesma usada na URL), de modo que trocar de edital não
 * descarta o que já foi baixado.
 *
 * Qualquer falha de armazenamento (cota estourada, navegação privativa,
 * localStorage desabilitado) é ignorada: o cache é um acelerador, nunca um
 * requisito para a tela funcionar.
 */
@Injectable({
    providedIn: 'root'
})
export class ParametrizacaoCacheStorage {

    /** O sufixo de versão permite invalidar tudo em uma nova publicação. */
    static readonly CHAVE_STORAGE = 'sicapes.parametrizacoes.v1';

    /** Tempo de validade de uma entrada. */
    static readonly TTL_MS = 12 * 60 * 60 * 1000;

    /** Limite de editais guardados, para não estourar a cota do localStorage. */
    static readonly MAXIMO_ENTRADAS = 5;

    /**
     * @returns A parametrização guardada e ainda válida, ou null.
     */
    public obter(chaveParametrizacao: string): any {
        if (!chaveParametrizacao) {
            return null;
        }

        const mapa = this.lerMapa();
        const entrada = mapa[chaveParametrizacao];

        if (!entrada) {
            return null;
        }

        if (this.expirou(entrada)) {
            delete mapa[chaveParametrizacao];
            this.escreverMapa(mapa);
            return null;
        }

        return entrada.dados;
    }

    public salvar(chaveParametrizacao: string, parametrizacao: any): void {
        if (!chaveParametrizacao || !parametrizacao) {
            return;
        }

        const mapa = this.lerMapa();

        // Reinserir move a chave para o fim: a ordem do objeto reflete a recência.
        delete mapa[chaveParametrizacao];
        mapa[chaveParametrizacao] = { salvoEm: Date.now(), dados: parametrizacao };

        this.escreverMapa(this.descartarExcedentes(mapa));
    }

    /** Remove a parametrização de um edital; sem argumento, limpa o cache inteiro. */
    public limpar(chaveParametrizacao: string = null): void {
        if (!chaveParametrizacao) {
            this.removerStorage();
            return;
        }

        const mapa = this.lerMapa();
        delete mapa[chaveParametrizacao];
        this.escreverMapa(mapa);
    }

    private expirou(entrada: ParametrizacaoCacheada): boolean {
        return !entrada.salvoEm || (Date.now() - entrada.salvoEm) > ParametrizacaoCacheStorage.TTL_MS;
    }

    /**
     * Mantém as entradas mais recentes quando o limite é ultrapassado. A ordem de
     * inserção do objeto é o critério de recência — mais confiável que `salvoEm`,
     * que empata quando duas gravações caem no mesmo milissegundo.
     */
    private descartarExcedentes(mapa: MapaParametrizacoesCacheadas): MapaParametrizacoesCacheadas {
        const chaves = Object.keys(mapa);

        if (chaves.length <= ParametrizacaoCacheStorage.MAXIMO_ENTRADAS) {
            return mapa;
        }

        const maisRecentes = chaves.slice(chaves.length - ParametrizacaoCacheStorage.MAXIMO_ENTRADAS);

        const reduzido: MapaParametrizacoesCacheadas = {};
        maisRecentes.forEach((chave) => reduzido[chave] = mapa[chave]);

        return reduzido;
    }

    private lerMapa(): MapaParametrizacoesCacheadas {
        try {
            const conteudo = localStorage.getItem(ParametrizacaoCacheStorage.CHAVE_STORAGE);
            const mapa = conteudo ? JSON.parse(conteudo) : {};

            return (mapa && typeof mapa === 'object') ? mapa : {};
        } catch (e) {
            // Conteúdo corrompido ou storage indisponível: recomeça vazio.
            this.removerStorage();
            return {};
        }
    }

    private escreverMapa(mapa: MapaParametrizacoesCacheadas): void {
        try {
            localStorage.setItem(ParametrizacaoCacheStorage.CHAVE_STORAGE, JSON.stringify(mapa));
        } catch (e) {
            // Cota estourada ou storage indisponível: segue sem cache.
            this.removerStorage();
        }
    }

    private removerStorage(): void {
        try {
            localStorage.removeItem(ParametrizacaoCacheStorage.CHAVE_STORAGE);
        } catch (e) {
            // Nada a fazer: o cache é opcional.
        }
    }
}
